import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, PageHeader } from '@erp/ui';
import { SlidersHorizontal } from '@/components/icons';
import { useAuth } from '@/lib/auth-context';

/**
 * Pull a friendly first name from the auth user — falls back to the email
 * local-part with its first letter uppercased so the greeting never shows
 * a blank or an awkward all-lowercase address. Matches the mobile app
 * convention so users see the same name on every device.
 */
function firstNameFrom(user?: { email?: string | null } | null): string {
  const local = user?.email?.split('@')[0] ?? '';
  if (!local) return '';
  const first = local.split(/[._-]/)[0] ?? local;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

import { SectionCards } from './dashboard/SectionCards';
import { ChartAreaInteractive } from './dashboard/ChartAreaInteractive';
import { RecentActivityTable } from './dashboard/RecentActivityTable';
import { PersonalizeDialog } from './dashboard/PersonalizeDialog';
import { PeriodComparisonSelector } from './dashboard/PeriodComparisonSelector';
import {
  DEFAULT_PERIOD_MODE,
  PERIOD_MODES,
  type PeriodMode,
} from './dashboard/period-range';
import { useDashboardWidgets } from './dashboard/registry';
import type { WidgetDef } from './dashboard/registry';

const PERIOD_MODE_STORAGE_KEY = 'dashboard.periodMode';

function readStoredPeriodMode(): PeriodMode {
  if (typeof window === 'undefined') return DEFAULT_PERIOD_MODE;
  const stored = window.localStorage.getItem(PERIOD_MODE_STORAGE_KEY);
  return stored && (PERIOD_MODES as readonly string[]).includes(stored)
    ? (stored as PeriodMode)
    : DEFAULT_PERIOD_MODE;
}

// Widgets by category
import {
  TopClientsWidget,
  UninvoicedDeliveriesWidget,
  SalesPipelineWidget,
  RecentInvoicesWidget,
  SalesYtdWidget,
  SalesWeeklyWidget,
  CreditNotesWidget,
} from './dashboard/widgets/sales-widgets';

import {
  MonthlyExpensesWidget,
  TopSuppliersWidget,
  UnbilledReceptionsWidget,
} from './dashboard/widgets/purchases-widgets';

import {
  TotalBankBalanceWidget,
  BankBalanceBreakdownWidget,
  OutstandingReceivablesWidget,
  OutstandingPayablesWidget,
  CashflowThisMonthWidget,
} from './dashboard/widgets/finance-widgets';

import {
  PortfolioValueWidget,
  PendingIssuedWidget,
  OverdueChecksWidget,
  DueSoonChecksWidget,
  TraitesByMonthWidget,
} from './dashboard/widgets/checks-widgets';

import {
  ArticlesTotalWidget,
  LowStockWidget,
  StockValuationWidget,
  TopCategoriesWidget,
  WarehousesWidget,
} from './dashboard/widgets/stock-widgets';

import {
  ClientsByTypeWidget,
  ClientsTotalWidget,
  SuppliersTotalWidget,
  RepresentativesWidget,
  OpenSessionsWidget,
  SessionsThisMonthWidget,
  WithholdingYtdWidget,
  PendingDeclarationsWidget,
} from './dashboard/widgets/operations-widgets';
import {
  PublishedProductsWidget,
  PendingOnlineOrdersWidget,
  NewOnlineCustomersWidget,
  OnlineSalesTodayWidget,
} from './dashboard/widgets/storefront-widgets';

// Map colSpan (1..4) → classes Tailwind responsives qui s'adaptent à chaque
// breakpoint de la grille (1 / 2 / 3 / 4 colonnes). colSpan=4 = pleine
// largeur à toutes les tailles.
function spanClassFor(colSpan: 1 | 2 | 3 | 4): string {
  switch (colSpan) {
    case 4: return 'sm:col-span-2 lg:col-span-3 xl:col-span-4';
    case 3: return 'sm:col-span-2 lg:col-span-3';
    case 2: return 'sm:col-span-2';
    default: return '';
  }
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [personalizeOpen, setPersonalizeOpen] = useState(false);
  const [periodMode, setPeriodMode] = useState<PeriodMode>(readStoredPeriodMode);
  const firstName = firstNameFrom(user);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PERIOD_MODE_STORAGE_KEY, periodMode);
  }, [periodMode]);

  // Widget registry — each entry registers id + category + render.
  // Order here is the order widgets appear on the dashboard.
  const registry = useMemo<WidgetDef[]>(() => [
    // --- Sales ---
    { id: 'sales-ytd',         category: 'sales',      titleKey: 'dashboard.salesYtd',            defaultVisible: true,  colSpan: 1, render: () => <SalesYtdWidget /> },
    { id: 'sales-weekly',      category: 'sales',      titleKey: 'dashboard.salesThisWeek',       defaultVisible: true,  colSpan: 1, render: () => <SalesWeeklyWidget /> },
    { id: 'pipeline',          category: 'sales',      titleKey: 'dashboard.pipelineBreakdown',   defaultVisible: true,  colSpan: 2, render: () => <SalesPipelineWidget /> },
    { id: 'top-clients',       category: 'sales',      titleKey: 'dashboard.topClientsThisMonth', defaultVisible: true,  colSpan: 2, render: () => <TopClientsWidget /> },
    { id: 'uninvoiced-bls',    category: 'sales',      titleKey: 'dashboard.uninvoicedBLs',       defaultVisible: true,  colSpan: 1, render: () => <UninvoicedDeliveriesWidget /> },
    { id: 'recent-invoices',   category: 'sales',      titleKey: 'dashboard.recentInvoices',      defaultVisible: false, colSpan: 2, render: () => <RecentInvoicesWidget /> },
    { id: 'credit-notes',      category: 'sales',      titleKey: 'dashboard.creditNotesMonth',    defaultVisible: false, colSpan: 1, render: () => <CreditNotesWidget /> },

    // --- Purchases ---
    { id: 'expenses-month',    category: 'purchases',  titleKey: 'dashboard.monthlyExpenses',     defaultVisible: true,  colSpan: 1, render: () => <MonthlyExpensesWidget /> },
    { id: 'top-suppliers',     category: 'purchases',  titleKey: 'dashboard.topSuppliersThisMonth', defaultVisible: true, colSpan: 2, render: () => <TopSuppliersWidget /> },
    { id: 'unbilled-rcpts',    category: 'purchases',  titleKey: 'dashboard.unbilledReceptions',  defaultVisible: false, colSpan: 1, render: () => <UnbilledReceptionsWidget /> },

    // --- Finance ---
    { id: 'bank-balance',      category: 'finance',    titleKey: 'dashboard.totalBankBalance',    defaultVisible: true,  colSpan: 1, render: () => <TotalBankBalanceWidget /> },
    { id: 'bank-breakdown',    category: 'finance',    titleKey: 'dashboard.bankBalanceBreakdown', defaultVisible: true, colSpan: 2, render: () => <BankBalanceBreakdownWidget /> },
    { id: 'cashflow-month',    category: 'finance',    titleKey: 'dashboard.cashflowThisMonth',   defaultVisible: true,  colSpan: 1, render: () => <CashflowThisMonthWidget /> },
    { id: 'receivables',       category: 'finance',    titleKey: 'dashboard.outstandingReceivables', defaultVisible: true, colSpan: 1, render: () => <OutstandingReceivablesWidget /> },
    { id: 'payables',          category: 'finance',    titleKey: 'dashboard.outstandingPayables', defaultVisible: true,  colSpan: 1, render: () => <OutstandingPayablesWidget /> },

    // --- Checks & Traites ---
    { id: 'portfolio',         category: 'checks',     titleKey: 'dashboard.portfolioValue',      defaultVisible: true,  colSpan: 1, render: () => <PortfolioValueWidget /> },
    { id: 'pending-issued',    category: 'checks',     titleKey: 'dashboard.pendingIssued',       defaultVisible: true,  colSpan: 1, render: () => <PendingIssuedWidget /> },
    { id: 'overdue-checks',    category: 'checks',     titleKey: 'dashboard.overdueChecks',       defaultVisible: true,  colSpan: 1, render: () => <OverdueChecksWidget /> },
    { id: 'due-soon-checks',   category: 'checks',     titleKey: 'dashboard.dueSoonChecks',       defaultVisible: true,  colSpan: 1, render: () => <DueSoonChecksWidget /> },
    { id: 'traites-by-month',  category: 'checks',     titleKey: 'dashboard.traitesByMonth',      defaultVisible: true,  colSpan: 4, render: () => <TraitesByMonthWidget /> },

    // --- Stock ---
    { id: 'articles-total',    category: 'stock',      titleKey: 'dashboard.articlesTotal',       defaultVisible: false, colSpan: 1, render: () => <ArticlesTotalWidget /> },
    { id: 'low-stock',         category: 'stock',      titleKey: 'dashboard.lowStock',            defaultVisible: true,  colSpan: 2, render: () => <LowStockWidget /> },
    { id: 'stock-valuation',   category: 'stock',      titleKey: 'dashboard.stockValuation',      defaultVisible: true,  colSpan: 1, render: () => <StockValuationWidget /> },
    { id: 'top-categories',    category: 'stock',      titleKey: 'dashboard.topCategoriesByArticles', defaultVisible: false, colSpan: 2, render: () => <TopCategoriesWidget /> },
    { id: 'warehouses-stat',   category: 'stock',      titleKey: 'dashboard.warehousesStat',      defaultVisible: false, colSpan: 1, render: () => <WarehousesWidget /> },

    // --- Operations (clients/suppliers/reps + caisse) ---
    { id: 'clients-total',     category: 'operations', titleKey: 'dashboard.clientsTotal',        defaultVisible: false, colSpan: 1, render: () => <ClientsTotalWidget /> },
    { id: 'clients-by-type',   category: 'operations', titleKey: 'dashboard.clientsByType',       defaultVisible: false, colSpan: 1, render: () => <ClientsByTypeWidget /> },
    { id: 'suppliers-total',   category: 'operations', titleKey: 'dashboard.suppliersTotal',      defaultVisible: false, colSpan: 1, render: () => <SuppliersTotalWidget /> },
    { id: 'reps-count',        category: 'operations', titleKey: 'dashboard.representativesStat', defaultVisible: false, colSpan: 1, render: () => <RepresentativesWidget /> },
    { id: 'open-sessions',     category: 'operations', titleKey: 'dashboard.openSessions',        defaultVisible: false, colSpan: 1, render: () => <OpenSessionsWidget /> },
    { id: 'sessions-month',    category: 'operations', titleKey: 'dashboard.sessionsThisMonth',   defaultVisible: false, colSpan: 1, render: () => <SessionsThisMonthWidget /> },

    // --- Withholding (RAS) ---
    { id: 'withholding-ytd',   category: 'withholding', titleKey: 'dashboard.withholdingYtd',      defaultVisible: false, colSpan: 1, render: () => <WithholdingYtdWidget /> },
    { id: 'pending-decls',     category: 'withholding', titleKey: 'dashboard.pendingDeclarations', defaultVisible: false, colSpan: 1, render: () => <PendingDeclarationsWidget /> },

    // --- Storefront / E-commerce ---
    { id: 'sf-published',      category: 'operations',  titleKey: 'storefront.widgets.publishedProducts',      defaultVisible: false, colSpan: 1, render: () => <PublishedProductsWidget /> },
    { id: 'sf-pending',        category: 'operations',  titleKey: 'storefront.widgets.pendingOrders',          defaultVisible: false, colSpan: 1, render: () => <PendingOnlineOrdersWidget /> },
    { id: 'sf-new-customers',  category: 'operations',  titleKey: 'storefront.widgets.newCustomersThisWeek',   defaultVisible: false, colSpan: 1, render: () => <NewOnlineCustomersWidget /> },
    { id: 'sf-sales-today',    category: 'operations',  titleKey: 'storefront.widgets.onlineSalesToday',       defaultVisible: false, colSpan: 1, render: () => <OnlineSalesTodayWidget /> },
  ], []);

  const { allWidgets, visibleWidgets, isVisible, toggle, showAll, hideAll, resetDefaults } =
    useDashboardWidgets(registry);

  return (
    <div className="@container/main flex flex-1 flex-col gap-2">
      <div className="flex flex-col gap-4 md:gap-6">
        <div className="px-4 pt-2 lg:px-6">
          <PageHeader
            title={
              firstName
                ? t('dashboard.greeting.morning', {
                    defaultValue: 'Bonjour, {{name}} 👋',
                    name: firstName,
                  })
                : t('dashboard.overview')
            }
            subtitle={t('dashboard.greeting.subtitle', {
              defaultValue: 'Voici ce qui se passe aujourd’hui.',
            })}
            actions={
              <div className="flex w-full min-w-0 flex-wrap items-center gap-2 lg:w-auto">
                <PeriodComparisonSelector
                  value={periodMode}
                  onChange={setPeriodMode}
                  className="min-w-0 flex-1 lg:flex-none"
                />
                <Button variant="outline" size="sm" onClick={() => setPersonalizeOpen(true)}>
                  <SlidersHorizontal size={14} />
                  {t('dashboard.personalize.button')}
                </Button>
              </div>
            }
          />
        </div>

        {/* Always-on overview strip (4 original KPIs) */}
        <SectionCards periodMode={periodMode} />

        {/* Revenue vs expenses chart */}
        <div className="px-4 lg:px-6">
          <ChartAreaInteractive />
        </div>

        {/* Personalizable widget grid — predictable breakpoints:
            1 col on phone, 2 at sm (≥640px), 3 at lg (≥1024px), 4 at xl (≥1280px).
            colSpan hints (2..4) scale progressively so wide widgets fill the
            row at each breakpoint. colSpan=4 → toujours pleine largeur. */}
        {visibleWidgets.length > 0 && (
          <div className="grid grid-cols-1 gap-4 px-4 sm:grid-cols-2 lg:grid-cols-3 lg:px-6 xl:grid-cols-4">
            {visibleWidgets.map((w) => {
              const spanClass = spanClassFor(w.colSpan);
              return (
                <div key={w.id} className={`min-w-0 h-full ${spanClass}`}>
                  {w.render()}
                </div>
              );
            })}
          </div>
        )}

        {/* Recent activity (always on) */}
        <div className="px-4 lg:px-6">
          <RecentActivityTable />
        </div>
      </div>

      <PersonalizeDialog
        open={personalizeOpen}
        onOpenChange={setPersonalizeOpen}
        allWidgets={allWidgets}
        isVisible={isVisible}
        toggle={toggle}
        showAll={showAll}
        hideAll={hideAll}
        resetDefaults={resetDefaults}
      />
    </div>
  );
}
